import * as signalR from "@microsoft/signalr";

import PrototypeInstance from "../helper/prototype-instance";

/**
 * Keeps the contact marked present while the portal is connected, so a technician's reply is held
 * for the three-minute acknowledgement window and emailed only if the contact has not seen it here
 * by then. Without it every reply was emailed at once: routing holds an email only for a contact
 * who is present, and only Messenger ever registered presence.
 *
 * The portal's presence is its own - PortalUser_{contactId}, one entry per connection - so neither
 * app's disconnect can clear the other's, and closing one portal tab leaves the others present. The
 * chat hub's RegisterPortalPresence target records it against this connection, and its disconnected
 * trigger removes exactly that entry when the connection drops, so there is nothing to undo here on
 * close. A logout calls unregisterPortalPresence first, since signing out alone does not drop it.
 *
 * Re-sent every 10 minutes (the entry lives 55) and when the tab comes back into view.
 */
const PRESENCE_INTERVAL_MS = 10 * 60 * 1000;

/** Coming back to the tab re-registers at once, but not more often than this. */
const VISIBILITY_THROTTLE_MS = 30 * 1000;

let hub: signalR.HubConnection | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;
let lastRegisteredAt = 0;

/**
 * The contact id travels as an argument because the portal's SignalR identity is the contact's
 * email, which carries none - as for AcknowledgePortalComment. Strings, as the trigger binds each
 * argument to a string parameter.
 */
async function registerPresence(): Promise<void> {
  if (hub?.state !== signalR.HubConnectionState.Connected) return;

  const getters = PrototypeInstance.globalProperties.$store?.getters;
  const contactId = Number(getters?.["ContactModule/getContactId"] ?? 0);
  const serviceProviderId = Number(getters?.["ContactModule/getServiceProviderId"] ?? 0);
  const email = String(getters?.["ContactModule/getContactEmail"] ?? "");
  if (!(contactId > 0) || !(serviceProviderId > 0)) return;

  lastRegisteredAt = Date.now();
  try {
    await hub.invoke(
      "RegisterPortalPresence",
      String(contactId),
      String(serviceProviderId),
      email,
    );
  } catch (e) {
    // Best effort: failing only means this contact's replies are emailed at once, as before.
    console.error("portal presence - RegisterPortalPresence failed", e);
  }
}

function onVisibilityChange(): void {
  if (
    document.visibilityState === "visible" &&
    Date.now() - lastRegisteredAt >= VISIBILITY_THROTTLE_MS
  ) {
    void registerPresence();
  }
}

/** Called whenever the portal's chat hub connection comes up - first connect and every reconnect,
 * each of which is a new server-side connection needing its own entry. */
export async function startPortalPresence(connection: signalR.HubConnection): Promise<void> {
  hub = connection;
  if (intervalId == null) {
    intervalId = setInterval(() => void registerPresence(), PRESENCE_INTERVAL_MS);
  }
  document.removeEventListener("visibilitychange", onVisibilityChange);
  document.addEventListener("visibilitychange", onVisibilityChange);
  await registerPresence();
}

/** Called on logout, before the connection is stopped. Closing the connection clears the entry too,
 * but a logout cannot rely on that alone: the server's UnregisterPortalPresence removes this
 * connection's entry at once and closes the connection, so nothing keeps being delivered to a
 * signed-out contact. Best effort - the stop that follows still ends the connection. */
export async function unregisterPortalPresence(): Promise<void> {
  const connection = hub;
  stopPortalPresence();
  if (connection?.state !== signalR.HubConnectionState.Connected) return;
  try {
    await connection.invoke("UnregisterPortalPresence");
  } catch (e) {
    console.error("portal presence - UnregisterPortalPresence failed", e);
  }
}

/** Called when the connection closes or the contact signs out. The server clears the entry on the
 * disconnect itself; this only stops re-registering. */
export function stopPortalPresence(): void {
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  document.removeEventListener("visibilitychange", onVisibilityChange);
  hub = null;
}
